import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Save, Upload, Trash2 } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MobileBannerSettings = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIos, setUploadingIos] = useState(false);
  const [uploadingAndroid, setUploadingAndroid] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    ios_image_url: '',
    ios_link: '',
    android_image_url: '',
    android_link: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/superadmin/banner-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching banner settings:', error);
      toast.error('Kunne ikke laste bannerinnstillinger');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/api/superadmin/banner-settings`,
        settings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Bannerinnstillinger lagret!');
    } catch (error) {
      console.error('Error saving banner settings:', error);
      toast.error('Kunne ikke lagre bannerinnstillinger');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (bannerType, file) => {
    if (!file) return;
    
    const setUploading = bannerType === 'ios' ? setUploadingIos : setUploadingAndroid;
    
    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('banner_type', bannerType);
      
      const response = await axios.post(
        `${API_BASE}/api/superadmin/upload-banner-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Update settings with new URL
      if (bannerType === 'ios') {
        setSettings({ ...settings, ios_image_url: response.data.url });
      } else {
        setSettings({ ...settings, android_image_url: response.data.url });
      }
      
      toast.success(`${bannerType.toUpperCase()} banner lastet opp!`);
    } catch (error) {
      console.error('Error uploading banner image:', error);
      toast.error('Kunne ikke laste opp bilde. Prøv igjen.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageDelete = async (bannerType) => {
    if (!window.confirm(`Er du sikker på at du vil slette ${bannerType.toUpperCase()} banneret?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE}/api/superadmin/delete-banner-image/${bannerType}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Clear URL from settings
      if (bannerType === 'ios') {
        setSettings({ ...settings, ios_image_url: '' });
      } else {
        setSettings({ ...settings, android_image_url: '' });
      }
      
      toast.success(`${bannerType.toUpperCase()} banner slettet!`);
    } catch (error) {
      console.error('Error deleting banner image:', error);
      toast.error('Kunne ikke slette bilde. Prøv igjen.');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
        <p className="text-gray-400 mt-2">Laster innstillinger...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg">
        <div>
          <h4 className="text-white font-medium">Vis mobilbannere</h4>
          <p className="text-gray-400 text-sm">Slå av og på visningen av bannerne på HJEM-siden</p>
        </div>
        <button
          onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
          className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
            settings.enabled ? 'bg-green-500' : 'bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
              settings.enabled ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* iOS Banner Settings */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">📱</span>
          <h4 className="text-white font-medium">iOS Banner</h4>
        </div>
        
        {/* Image Upload Section */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Last opp iOS banner-bilde
          </label>
          <div className="flex gap-2">
            <label className="flex-1 cursor-pointer">
              <div className="flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors">
                <Upload className="h-4 w-4 mr-2" />
                {uploadingIos ? 'Laster opp...' : 'Velg bilde'}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload('ios', e.target.files[0])}
                disabled={uploadingIos}
                className="hidden"
              />
            </label>
            {settings.ios_image_url && (
              <button
                onClick={() => handleImageDelete('ios')}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Slett
              </button>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Eller bruk ekstern bilde-URL
          </label>
          <input
            type="text"
            value={settings.ios_image_url}
            onChange={(e) => setSettings({ ...settings, ios_image_url: e.target.value })}
            placeholder="https://example.com/ios-banner.png"
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:border-yellow-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Lenke URL (Vimeo/YouTube)
          </label>
          <input
            type="text"
            value={settings.ios_link}
            onChange={(e) => setSettings({ ...settings, ios_link: e.target.value })}
            placeholder="https://vimeo.com/1129510748"
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:border-yellow-500 focus:outline-none"
          />
          <p className="text-gray-400 text-xs mt-1">Lenke til instruksjonsvideo som åpnes i nytt vindu</p>
        </div>

        {/* Preview */}
        {settings.ios_image_url && (
          <div className="mt-4">
            <p className="text-sm text-gray-400 mb-2">Forhåndsvisning:</p>
            <img
              src={settings.ios_image_url}
              alt="iOS banner preview"
              className="w-full max-w-md rounded-lg border border-gray-600"
              onError={(e) => {
                e.target.src = '';
                e.target.alt = 'Kunne ikke laste bilde';
              }}
            />
          </div>
        )}
      </div>

      {/* Android Banner Settings */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">🤖</span>
          <h4 className="text-white font-medium">Android Banner</h4>
        </div>
        
        {/* Image Upload Section */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Last opp Android banner-bilde
          </label>
          <div className="flex gap-2">
            <label className="flex-1 cursor-pointer">
              <div className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors">
                <Upload className="h-4 w-4 mr-2" />
                {uploadingAndroid ? 'Laster opp...' : 'Velg bilde'}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload('android', e.target.files[0])}
                disabled={uploadingAndroid}
                className="hidden"
              />
            </label>
            {settings.android_image_url && (
              <button
                onClick={() => handleImageDelete('android')}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Slett
              </button>
            )}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Eller bruk ekstern bilde-URL
          </label>
          <input
            type="text"
            value={settings.android_image_url}
            onChange={(e) => setSettings({ ...settings, android_image_url: e.target.value })}
            placeholder="https://example.com/android-banner.png"
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:border-yellow-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Lenke URL (Vimeo/YouTube)
          </label>
          <input
            type="text"
            value={settings.android_link}
            onChange={(e) => setSettings({ ...settings, android_link: e.target.value })}
            placeholder="https://vimeo.com/1129510730"
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:border-yellow-500 focus:outline-none"
          />
          <p className="text-gray-400 text-xs mt-1">Lenke til instruksjonsvideo som åpnes i nytt vindu</p>
        </div>

        {/* Preview */}
        {settings.android_image_url && (
          <div className="mt-4">
            <p className="text-sm text-gray-400 mb-2">Forhåndsvisning:</p>
            <img
              src={settings.android_image_url}
              alt="Android banner preview"
              className="w-full max-w-md rounded-lg border border-gray-600"
              onError={(e) => {
                e.target.src = '';
                e.target.alt = 'Kunne ikke laste bilde';
              }}
            />
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-5 w-5 mr-2" />
          {saving ? 'Lagrer...' : 'Lagre innstillinger'}
        </button>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-600/20 border border-blue-500/50 rounded-lg">
        <h4 className="text-blue-400 font-medium text-sm mb-2">💡 Slik bruker du denne funksjonen:</h4>
        <ol className="text-blue-300 text-xs space-y-1 list-decimal list-inside">
          <li>Last opp bannere til en ekstern tjeneste (f.eks. Imgur, Cloudinary, eller Emergent Assets)</li>
          <li>Lim inn bilde-URL-ene i feltene over</li>
          <li>Legg inn lenker til instruksjonsvideoer (Vimeo eller YouTube)</li>
          <li>Klikk "Lagre innstillinger"</li>
          <li>Bannerne vises nå på HJEM-siden for alle medlemmer og skyteledere</li>
          <li>Du kan når som helst slå av/på bannerne eller bytte ut bildene</li>
        </ol>
      </div>
    </div>
  );
};

export default MobileBannerSettings;
